import { useMemo, useState } from 'react';
import { Button } from 'antd';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { InfoDot, NumInput, MoneyInput } from './MortgagePage';
import { num, maxLoanFromInstallment } from '../lib/loanCalc';

// Affordability — inverse of the mortgage instalment formula: the property price
// (loan at 100% financing) a desired monthly payment can service. Recomputes live
// as inputs change (reference tool has no Calculate button).

const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DEFAULTS = { payment: '', ratePct: '3.80', years: '30' };

export default function AffordabilityPage() {
  const [f, setF] = useState(DEFAULTS);
  const [infoOpen, setInfoOpen] = useState(false);
  const patch = (p) => setF((x) => ({ ...x, ...p }));

  const price = useMemo(
    () => maxLoanFromInstallment(f.payment, f.ratePct, f.years),
    [f]
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Affordability" right={<InfoDot onClick={() => setInfoOpen(true)} />} />

      {/* live headline result */}
      <div style={{ textAlign: 'center', padding: '18px 16px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Property Price</div>
        <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>
          {num(f.payment) > 0 && price > 0 ? `RM${fmt2(price)}` : 'RM0.00'}
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Monthly Payment">
          <MoneyInput value={f.payment} onChange={(v) => patch({ payment: v })} placeholder="0" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Interest Rate (%)">
            <NumInput value={f.ratePct} onChange={(v) => patch({ ratePct: v })} placeholder="3.80" />
          </Field>
          <Field label="Loan Tenure (Year)">
            <NumInput value={f.years} onChange={(v) => patch({ years: v })} placeholder="30" />
          </Field>
        </div>
        <Button block size="large" onClick={() => setF(DEFAULTS)}>Reset</Button>
      </div>

      <InfoNotes
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Affordability"
        pages={[
          { body: 'Determine how much you can afford for a property. By entering your desired monthly payment, interest rate and loan period, to estimate the loan amount that works with your budget.' },
          { title: 'Basis', body: 'The property price shown is the loan a standard amortised instalment can service at the given rate and tenure (100% financing). Add your intended down payment on top for the full purchase budget, and check the DSR tool for bank eligibility.' },
        ]}
      />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);
